/**
 * Chainable, immutable query builder.
 *
 * Each builder operation returns a NEW Query — the underlying plan is never
 * mutated. This makes plans safe to share, cache, and serialize.
 */

import type { QueryField } from '../types.js'
import type { Clause, CrossJoinClause, FieldClause, FilterClause, GroupClause, Operator, WherePredicateClause } from './predicate.js'
import { evaluateClause, hasFnClause } from './predicate.js'
import type { CollectionIndexes } from '../../with-lookup/indexing/eager-indexes.js'
import type { JoinableSource, JoinContext, JoinLeg, JoinStrategy } from './join.js'
import { applyJoins } from './join.js'
import { CrossJoinTooLargeError, CrossJoinSourceUnknownError, FieldNotQueryableError } from '../errors.js'
import type { LiveQuery, LiveUpstream } from './live.js'
import { buildLiveQuery } from './live.js'
import type { AggregateSpec, AggregateResult, AggregationUpstream, Aggregation } from '../../with-lookup/aggregate/aggregation.js'
import type { ReducerBuilder } from '../../with-lookup/aggregate/reducers.js'
import { reducerBuilder } from '../../with-lookup/aggregate/reducers.js'
import type { GroupedQuery, GroupedQueryN } from '../../with-lookup/aggregate/groupby.js'
import { NO_AGGREGATE, type AggregateStrategy } from '../../with-lookup/aggregate/strategy.js'
import type { ViaPipeline } from '../via/pipeline.js'

export interface OrderBy {
  readonly field: string
  readonly direction: 'asc' | 'desc'
  /**
   * Sort key for a `dictKey`/`staticDict` field: `'value'` (default)
   * sorts by the stored code; `'label'` sorts by the code's resolved label at
   * the query locale (`toArray({ locale })`, or a `staticDict` `displayLocale`).
   * Falls back to the code when no label resolves.
   */
  readonly by?: 'value' | 'label'
}

/**
 * A complete query plan: zero-or-more clauses, optional ordering, pagination,
 * and optional joins.
 *
 * Plans are JSON-serializable as long as no FilterClause is present and no
 * join leg carries a manual `strategy` override (JoinLeg itself is plain
 * data, so it serializes cleanly).
 *
 * Plans are intentionally NOT parametric on T — see `predicate.ts` FilterClause
 * for the variance reasoning. The public `Query<T>` API attaches the type tag.
 */
export interface QueryPlan {
  readonly clauses: readonly Clause[]
  readonly orderBy: readonly OrderBy[]
  readonly limit: number | undefined
  readonly offset: number
  /**
   * Zero-or-more join legs to apply after where/orderBy/limit/offset.
   * Each leg attaches a resolved right-side record (or null) under its
   * alias. See `query/join.ts` for the full semantics.
   */
  readonly joins: readonly JoinLeg[]
}

const EMPTY_PLAN: QueryPlan = {
  clauses: [],
  orderBy: [],
  limit: undefined,
  offset: 0,
  joins: [],
}

/** Default row ceiling for cross-join expansion. Matches JoinTooLargeError's ceiling. */
export const DEFAULT_CROSS_JOIN_MAX_ROWS = 50_000

/**
 * Source of records that a query executes against.
 *
 * The interface is non-parametric to keep variance friendly: callers cast
 * their typed source (e.g. `QuerySource<Invoice>`) into this opaque shape.
 *
 * `getIndexes` and `lookupById` are optional fast-path hooks. When both are
 * present and a where clause matches an indexed field, the executor uses
 * the index to skip a linear scan. Sources without these methods (or with
 * `getIndexes` returning `null`) always fall back to a linear scan.
 */
export interface QuerySource<T> {
  /** Snapshot of all current records. The query never mutates this array. */
  snapshot(): readonly T[]
  /** Subscribe to mutations; returns an unsubscribe function. */
  subscribe?(cb: () => void): () => void
  /** Index store for the indexed-fast-path. Optional. */
  getIndexes?(): CollectionIndexes | null
  /** O(1) record lookup by id, used to materialize index hits. */
  lookupById?(id: string): T | undefined
  /**
   * The backing collection's compiled Via pipeline (money now; more Via
   * features later), used to rewrite `where()` operands, decode results,
   * order, and rewrite aggregate reducers for covered fields.
   */
  via?: ViaPipeline
  /**
   * Id-paired snapshot for `Query._idArray()` (the `retrieve({within})`
   * id projection). Optional: only collection-backed queries supply it.
   */
  snapshotEntries?(): readonly { id: string; record: T }[]
}

interface InternalSource {
  snapshot(): readonly unknown[]
  subscribe?(cb: () => void): () => void
  getIndexes?(): CollectionIndexes | null
  lookupById?(id: string): unknown
  via?: ViaPipeline
  snapshotEntries?(): readonly { id: string; record: unknown }[]
}

/**
 * The chainable builder. All methods return a new Query — the original
 * remains unchanged. Terminal methods (`toArray`, `first`, `count`,
 * `subscribe`) execute the plan against the source.
 *
 * Type parameter T flows through the public API for ergonomics, but the
 * internal storage uses `unknown` so Collection<T> stays covariant.
 *
 * The optional `joinContext` is attached when the Query is constructed
 * via `Collection.query()` (Collection passes in a context built from
 * the Vault's join resolver). A Query constructed via `new Query`
 * directly — e.g. from tests with a plain-object source — has no
 * joinContext, and calling `.join()` on it throws with an actionable
 * error. See `query/join.ts` for the full design.
 */
/**
 * Declared deterministic predicate. Carries the consumer's
 * stable `hash` (for function-body identity), the function itself,
 * and is keyed by name when registered on a `Query<T>` via
 * `_withPredicates()`.
 */
export interface DeclaredPredicate {
  hash: string
  fn: (record: unknown, ctx?: unknown) => boolean
}

export class Query<T, S extends keyof T = never, Q extends keyof T & string = never, M extends keyof T & string = never> {
  private readonly source: InternalSource
  private readonly plan: QueryPlan
  private readonly joinContext: JoinContext | undefined
  private readonly aggregateStrategy: AggregateStrategy
  private readonly predicates: ReadonlyMap<string, DeclaredPredicate> | undefined

  constructor(
    source: QuerySource<T>,
    plan: QueryPlan = EMPTY_PLAN,
    joinContext?: JoinContext,
    aggregateStrategy: AggregateStrategy = NO_AGGREGATE,
    predicates?: ReadonlyMap<string, DeclaredPredicate>,
  ) {
    this.source = source as InternalSource
    this.plan = plan
    this.joinContext = joinContext
    this.aggregateStrategy = aggregateStrategy
    this.predicates = predicates
  }

  /**
   * @internal — accessor for the materialized-view dependency
   * analyzer. Not part of the public API; consumers should use the
   * builder methods, not inspect the plan directly.
   */
  _plan(): QueryPlan {
    return this.plan
  }

  /**
   * @internal — accessor for the materialized-view dependency
   * analyzer. Returns the join resolution context (or `undefined` for
   * queries constructed without a Collection backing).
   */
  _joinContext(): JoinContext | undefined {
    return this.joinContext
  }

  /**
   * @internal — clone this Query with a declared-predicate map
   * attached. Used by the materialized-view registry to enable
   * `.wherePredicate(name, ctx?)` for the MV's query callback.
   * Consumers don't call this directly.
   */
  _withPredicates(predicates: ReadonlyMap<string, DeclaredPredicate>): Query<T, S, Q, M> {
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      this.plan,
      this.joinContext,
      this.aggregateStrategy,
      predicates,
    )
  }

  /**
   * @internal — the ids of records matching this query's plan,
   * recovered by reference identity: `executePlanWithSource` returns the
   * ORIGINAL snapshot record references (money-decode and joins are applied
   * later, in `toArray`), so each matched record is found in the id-paired
   * `snapshotEntries()` map. Used by `collection.retrieve({ within })`.
   * Throws if the source is not collection-backed (no `snapshotEntries`).
   */
  _idArray(): string[] {
    const entries = this.source.snapshotEntries?.()
    if (entries === undefined) {
      throw new Error(
        'Query._idArray(): the query source has no snapshotEntries(); ' +
          'retrieve({ within }) requires a collection-backed query (collection.query()).',
      )
    }
    if (this.plan.clauses.some(c => c.type === 'crossJoin')) {
      throw new Error(
        'Query._idArray(): retrieve({ within }) does not support crossJoin queries ' +
          '(cross-join produces new row objects, breaking id recovery). ' +
          'Use where/filter/and/or, or a projection .join().',
      )
    }
    const refToId = new Map<unknown, string>()
    for (const { id, record } of entries) refToId.set(record, id)
    const matched = executePlanWithSource(this.source, this.plan, this.joinContext)
    const ids: string[] = []
    for (const r of matched) {
      const id = refToId.get(r)
      if (id !== undefined) ids.push(id)
    }
    return ids
  }

  /**
   * Filter by a registered deterministic predicate. Requires
   * the Query to have been augmented with a predicates map (typically
   * via the materialized-view registry — bare Queries constructed
   * outside an MV throw on `.wherePredicate()`).
   *
   * `ctx` is an optional opaque value passed verbatim to the predicate
   * function. Both `predicateHash` (from the registration) and a
   * canonical-JSON hash of `ctx` fold into the MV's `queryHash`, so
   * either changing forces refresh on next visit.
   */
  wherePredicate(name: string, ctx?: unknown): Query<T, S, Q, M> {
    if (!this.predicates) {
      throw new Error(
        `.wherePredicate("${name}"): no predicates registered on this Query. ` +
          `Function-based predicates require the Query to be obtained from ` +
          `inside a materialized-view query() callback whose strategy declares ` +
          `\`predicates: { ${name}: { hash, fn } }\`.`,
      )
    }
    const decl = this.predicates.get(name)
    if (!decl) {
      throw new Error(
        `.wherePredicate("${name}"): predicate not registered. ` +
          `Available: ${[...this.predicates.keys()].join(', ') || '(none)'}.`,
      )
    }
    const clause: WherePredicateClause = {
      type: 'wherePredicate',
      name,
      ctx,
      predicateHash: decl.hash,
      ctxHash: canonicalCtxHash(ctx),
      fn: decl.fn,
    }
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, clauses: [...this.plan.clauses, clause] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Add a field comparison. Multiple where() calls are AND-combined.
   *
   * A declared money field compares in MAJOR units: the operand
   * (`10000`, `'10000.00'`, or `{ amount, currency }` in multi mode) is
   * quantized into stored scaled-int space at build time and evaluated
   * BigInt-exact per record. A malformed operand or a string operator
   * (`contains`/`startsWith`) throws here, at the call site.
   *
   * Consults the Via pipeline's posture before building a clause (#629
   * Task 8): a field whose posture is `queryable: 'none'` (e.g. a
   * `blobFields` slot) throws `FieldNotQueryableError` here, at the call
   * site. Every other posture is unaffected — the existing per-binding
   * `buildClause` machinery runs exactly as before.
   */
  where(field: QueryField<T, S, Q>, op: Operator, value: unknown): Query<T, S, Q, M> {
    const via = this.source.via
    if (via?.postureFor(field)?.queryable === 'none') throw new FieldNotQueryableError(field)
    const viaClause = via?.buildClause(field, op, value)
    const clause: FieldClause = viaClause
      ? {
          type: 'field',
          field,
          op,
          value,
          via: {
            brand: viaClause.brand,
            payload: viaClause.payload,
            evaluate: (actual: unknown, evalOp: string) => via!.evaluateClause(viaClause, actual, evalOp),
            indexValue: via!.indexProbe(viaClause, op),
          },
        }
      : { type: 'field', field, op, value }
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, clauses: [...this.plan.clauses, clause] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Logical OR group. Pass a callback that builds a sub-query.
   * Each clause inside the callback is OR-combined; the group itself
   * joins the parent plan with AND.
   */
  or(builder: (q: Query<T, S, Q, M>) => Query<T, S, Q, M>): Query<T, S, Q, M> {
    const sub = builder(
      new Query<T, S, Q, M>(this.source as QuerySource<T>, EMPTY_PLAN, this.joinContext, this.aggregateStrategy, this.predicates),
    )
    const group: GroupClause = {
      type: 'group',
      op: 'or',
      clauses: sub.plan.clauses,
    }
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, clauses: [...this.plan.clauses, group] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Logical AND group. Same shape as `or()` but every clause inside the group
   * must match. Useful for explicit grouping inside a larger OR.
   */
  and(builder: (q: Query<T, S, Q, M>) => Query<T, S, Q, M>): Query<T, S, Q, M> {
    const sub = builder(
      new Query<T, S, Q, M>(this.source as QuerySource<T>, EMPTY_PLAN, this.joinContext, this.aggregateStrategy, this.predicates),
    )
    const group: GroupClause = {
      type: 'group',
      op: 'and',
      clauses: sub.plan.clauses,
    }
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, clauses: [...this.plan.clauses, group] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /** Escape hatch: add an arbitrary predicate function. Not serializable. */
  filter(fn: (record: T) => boolean): Query<T, S, Q, M> {
    const clause: FilterClause = {
      type: 'filter',
      fn: fn as (record: unknown) => boolean,
    }
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, clauses: [...this.plan.clauses, clause] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Sort by a field. Subsequent calls are tie-breakers. Pass
   * `{ by: 'label' }` to sort a `dictKey`/`staticDict` field by its resolved
   * label at the query locale instead of the stored code.
   *
   * Consults the Via pipeline's posture (#629 Task 8): a field whose posture
   * is `queryable: 'none'` throws `FieldNotQueryableError` here, at the call
   * site — same gate as `where()`.
   */
  orderBy(field: QueryField<T, S>, direction: 'asc' | 'desc' = 'asc', opts?: { by?: 'value' | 'label' }): Query<T, S, Q, M> {
    if (this.source.via?.postureFor(field)?.queryable === 'none') throw new FieldNotQueryableError(field)
    const entry: OrderBy = opts?.by === 'label' ? { field, direction, by: 'label' } : { field, direction }
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, orderBy: [...this.plan.orderBy, entry] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /** Cap the result size. */
  limit(n: number): Query<T, S, Q, M> {
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, limit: n },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /** Skip the first N matching records (after ordering). */
  offset(n: number): Query<T, S, Q, M> {
    return new Query<T, S, Q, M>(
      this.source as QuerySource<T>,
      { ...this.plan, offset: n },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Resolve a `ref()`-declared foreign key and attach the right-side
   * record under `opts.as`. — eager, single-FK, intra-
   * vault joins.
   *
   * ```ts
   * const rows = invoices.query()
   *   .where('status', '==', 'open')
   *   .join('clientId', { as: 'client' })
   *   .toArray()
   * // → [{ id, amount, client: { id, name, ... } }, ...]
   * ```
   *
   * Preconditions:
   *   - The Query must have a `joinContext` (constructed via
   *     `Collection.query()`, not `new Query`).
   *   - `field` must have a matching `refs: { [field]: ref('<target>') }`
   *     declaration on the left collection.
   *   - The target collection must be reachable via the vault
   *     (either currently open or openable on demand).
   *
   * Strategy:
   *   - Nested-loop against `lookupById` when the target source
   *     provides it (the common path for Collection targets).
   *   - Hash join otherwise, or when `{ strategy: 'hash' }` is
   *     explicitly passed for test purposes.
   *
   * Ref-mode semantics on dangling refs (left record has a non-null
   * FK value pointing at a right-side id that doesn't exist):
   *   - `strict`  → throws `DanglingReferenceError` with the full
   *     field / target / refId context.
   *   - `warn`    → attaches `null` and emits a one-shot warning per
   *     unique dangling pair.
   *   - `cascade` → attaches `null` silently. Cascade is a
   *     delete-time mode; dangling refs visible at read time are
   *     either mid-flight cascades or pre-existing orphans, not a
   *     DSL-level error.
   *
   * A left-side record whose FK field is `null` / `undefined` is NOT
   * a dangling ref — it's "no reference at all", always allowed
   * regardless of mode.
   *
   * The return type widens `T` with `Record<As, R | null>`. The `R`
   * parameter is optional — supply it explicitly for type-checked
   * access to the joined fields:
   *
   * ```ts
   * invoices.query().join<'client', Client>('clientId', { as: 'client' })
   * //                 ^^^^^^^^^^^^^^^^^^^ alias literal + right-side type
   * ```
   *
   * Without the generic, the joined field is typed as `unknown`, which
   * still works but requires a cast to access its properties.
   *
   * Joins stay intra-vault by construction — cross-vault
   * correlation goes through `Noydb.queryAcross`, not
   * `.join()`.
   */
  join<As extends string, R = unknown>(
    field: QueryField<T, S>,
    opts: { as: As; strategy?: JoinStrategy; maxRows?: number },
  ): Query<T & Record<As, R | null>, S, Q, M> {
    if (!this.joinContext) {
      throw new Error(
        `Query.join() requires a join context. Use collection.query() ` +
          `to construct a join-capable Query instead of the Query constructor ` +
          `directly (the direct constructor is only used for tests with ` +
          `plain-object sources).`,
      )
    }
    const descriptor = this.joinContext.resolveRef(field)
    // Check for dictKey join when no ref() is declared
    const isDictJoinField = !descriptor && this.joinContext.resolveDictSource?.(field) != null
    if (!descriptor && !isDictJoinField) {
      throw new Error(
        `Query.join(): no ref() declared for field "${field}" on collection ` +
          `"${this.joinContext.leftCollection}". Add ` +
          `refs: { ${field}: ref('<target-collection>') } to the collection ` +
          `options, then retry. See the ref() docs for the full list of modes.`,
      )
    }
    const leg: JoinLeg = descriptor
      ? {
          field,
          as: opts.as,
          target: descriptor.target,
          mode: descriptor.mode,
          strategy: opts.strategy,
          maxRows: opts.maxRows,
          //  constraint #1 — always 'all' in. Do not remove.
          partitionScope: 'all',
        }
      : {
          // Dict join leg
          field,
          as: opts.as,
          target: field, // dict name = field name for dictKey
          mode: 'strict',
          strategy: opts.strategy,
          maxRows: opts.maxRows,
          partitionScope: 'all',
          isDictJoin: true,
        }
    return new Query<T & Record<As, R | null>, S, Q, M>(
      this.source as unknown as QuerySource<T & Record<As, R | null>>,
      { ...this.plan, joins: [...this.plan.joins, leg] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Cartesian-product cross-join against `target` collection. Each result row
   * carries the original `T` fields plus `result[as]` populated from every
   * right-side row (or the filtered subset when `on:` is supplied).
   *
   * **Order matters:** `.where().crossJoin()` filters BEFORE expanding (cheaper);
   * `.crossJoin().where('alias.field', ...)` filters AFTER (required when the
   * where clause references the aliased fields).
   *
   * **Cost ceiling:** `CrossJoinTooLargeError` fires before allocation when
   * `leftRows × rightRows` (or the cumulative lateral count) exceeds the limit.
   * Default: 50,000 rows. Override per-clause with `{ maxRows: N }`.
   *
   * **`on:` shapes:**
   *   - `on: (left) => TTarget[]`              — subset form (most efficient)
   *   - `on: (left) => (right) => boolean`     — predicate form
   *   - `on: { predicate: 'name' }`            — MV-safe, hash-tracked form
   *     (requires the Query to have been augmented via `_withPredicates`)
   *
   * Requires a JoinContext (constructed via `collection.query()`).
   */
  crossJoin<TTarget = unknown, As extends string = string>(
    target: string,
    opts: {
      as: As
      on?:
        | ((left: T) => unknown[] | ((right: TTarget) => boolean))
        | { readonly predicate: string }
      maxRows?: number
    },
  ): Query<T & { [K in As]: TTarget }, S, Q, M> {
    if (!this.joinContext) {
      throw new Error(
        `Query.crossJoin("${target}"): requires a join context. ` +
          `Use collection.query() to construct a cross-join-capable Query instead of ` +
          `the Query constructor directly.`,
      )
    }

    let onFn: CrossJoinClause['on']
    let onPredicateName: string | undefined

    if (opts.on !== undefined) {
      if (typeof opts.on === 'function') {
        onFn = opts.on as CrossJoinClause['on']
        if (this.predicates) {
          console.warn(
            `Query.crossJoin("${target}", { on: callback }): inline on: callback inside a ` +
              `withMaterializedView query() disables queryHash drift detection for this cross-join. ` +
              `Use on: { predicate: '<name>' } to enable it.`,
          )
        }
      } else {
        const predName = (opts.on as { predicate: string }).predicate
        if (!this.predicates) {
          throw new Error(
            `Query.crossJoin("${target}", { on: { predicate: "${predName}" } }): ` +
              `the { predicate } form requires a predicates map. ` +
              `Use this form inside a withMaterializedView query() callback that declares ` +
              `predicates: { ${predName}: { hash, fn } }.`,
          )
        }
        const decl = this.predicates.get(predName)
        if (!decl) {
          throw new Error(
            `Query.crossJoin("${target}"): predicate "${predName}" not registered. ` +
              `Available: ${[...this.predicates.keys()].join(', ') || '(none)'}.`,
          )
        }
        const as = opts.as
        const predicateFn = decl.fn
        onFn = (_left: unknown): ((right: unknown) => boolean) =>
          (right: unknown) =>
            predicateFn({ ...(_left as Record<string, unknown>), [as]: right })
        onPredicateName = predName
      }
    }

    const clause: CrossJoinClause = {
      type: 'crossJoin',
      target,
      as: opts.as,
      ...(onFn !== undefined && { on: onFn }),
      ...(onPredicateName !== undefined && { onPredicateName }),
      ...(opts.maxRows !== undefined && { maxRows: opts.maxRows }),
    }

    return new Query<T & { [K in As]: TTarget }, S, Q, M>(
      this.source as unknown as QuerySource<T & { [K in As]: TTarget }>,
      { ...this.plan, clauses: [...this.plan.clauses, clause] },
      this.joinContext,
      this.aggregateStrategy,
      this.predicates,
    )
  }

  /**
   * Execute the plan and return the matching records. When the plan
   * carries any join legs, they are applied after `where` / `orderBy`
   * / `limit` / `offset` narrow the left set. See the `.join()` doc
   * for the ordering rationale.
   *
   * `opts.locale` resolves JOINED right-side i18n fields at the
   * `join` layer to that locale; without it, the owning collection's default
   * locale applies, and a locale-less query leaves joined i18n fields raw.
   * (Left/base i18n fields are resolved by `get`/`list`, not here.)
   */
  toArray(opts?: { locale?: string }): T[] {
    // Decode Via-covered fields (e.g. money: stored scaled-int → canonical
    // decimal) so query().toArray() matches get()/sum(), which already
    // apply the same decode. Decode the left/base records before joins
    // (right-side aliased fields belong to other collections and are out
    // of this source's Via scope).
    const base = this.decodeVia(executePlanWithSource(this.source, this.plan, this.joinContext, opts?.locale))
    if (this.plan.joins.length === 0) return base as T[]
    if (!this.joinContext) {
      // Unreachable in practice — .join() throws if joinContext is
      // missing — but belt-and-braces for direct plan construction.
      throw new Error(
        `Query.toArray(): plan carries ${this.plan.joins.length} join leg(s) ` +
          `but no JoinContext is attached. This usually means the Query was ` +
          `constructed via the raw Query constructor with a plan that had joins ` +
          `pre-populated. Use collection.query().join(...) instead.`,
      )
    }
    return applyJoins(base, this.plan.joins, this.joinContext, opts?.locale) as T[]
  }

  /**
   * Decode this source's Via-covered fields on read (e.g. money: stored
   * scaled-int → canonical decimal), so `query().toArray()` agrees with
   * `get()`/`sum()` on the value. No-op when the source's Via pipeline
   * declares no result decode.
   *
   * The query layer carries no locale context, so money decodes with
   * `'raw'` — canonical decimal, WITHOUT fabricating locale-formatted
   * `<field>Formatted` / `<field>Number` virtuals. Producing a
   * guessed-locale string here would reintroduce a "two read paths
   * disagree" failure on the virtual field (e.g. it-IT via `get()` vs
   * en-US here). Consumers who need formatted money read through
   * `get()`/`list()` with a locale.
   */
  private decodeVia(records: readonly unknown[]): unknown[] {
    const via = this.source.via
    if (!via || !via.hasResultDecode) return records as unknown[]
    return records.map(r => via.decodeResults(r))
  }

  /** Return the first matching record, or null. Joins are applied. `opts.locale` resolves joined i18n fields. */
  first(opts?: { locale?: string }): T | null {
    const arr = this.limit(1).toArray(opts)
    return arr[0] ?? null
  }

  /**
   * Return the number of matching records (after where/filter,
   * before limit). **Joins are NOT applied** — count() reports the
   * left-side cardinality, because joins in are projection-only
   * (they attach an aliased field; they never filter). Running joins
   * here just to discard the aliases would be wasteful, and in strict
   * mode it could throw `DanglingReferenceError` for a call whose
   * intent is purely to count.
   */
  count(): number {
    if (this.plan.clauses.some(c => c.type === 'crossJoin')) {
      if (!this.joinContext) {
        throw new Error(
          `Query.count(): plan contains crossJoin clauses but no JoinContext is attached.`,
        )
      }
      return executeClausePipeline(this.source, this.plan.clauses, this.joinContext).length
    }
    // Use the same index-aware candidate machinery as toArray(); skip the
    // index-driving clause from re-evaluation. The length BEFORE limit/offset
    // is what `count()` documents.
    const { candidates, remainingClauses } = candidateRecords(this.source, this.plan.clauses)
    if (remainingClauses.length === 0) return candidates.length
    return filterRecords(candidates, remainingClauses, fnViewDecoder(this.source)).length
  }

  /**
   * Reduce the matching records through a named set of reducers.
   * the aggregation terminal.
   *
   * ```ts
   * const { total, n, avgAmount } = invoices.query()
   *   .where('status', '==', 'open')
   *   .aggregate({
   *     total:     sum('amount'),
   *     n:         count(),
   *     avgAmount: avg('amount'),
   *   })
   *   .run()
   * ```
   *
   * Returns an `Aggregation<R>` wrapper with two terminals:
   *   - `.run(): R` — synchronous one-shot reduction
   *   - `.live(): LiveAggregation<R>` — reactive primitive that
   *     re-runs the reduction whenever the source notifies of a
   *     change. Always call `live.stop()` when finished.
   *
   * The reducer spec is bound here once and reused by both
   * terminals — this is why `.aggregate()` returns a wrapper instead
   * of being a direct terminal. Consumers who only need the static
   * value read `.run()`; consumers wiring a reactive UI read
   * `.live()`.
   *
   * Joins are intentionally NOT applied to aggregations in —
   * the same logic as `.count()`. Joins in are projection-only
   * (they attach an aliased field and never filter), so running
   * them just to throw the aliases away would be wasteful. If you
   * need a reducer that reads a joined field, open an issue —
   * aggregations-across-joins is explicitly out of scope for v1.
   *
   * Every reducer factory accepts an optional `{ seed }` parameter
   * that is plumbed through the protocol but unused by the
   * executor — that's  constraint #2. When partition-aware
   * aggregation lands, the seed will carry running state across
   * partition boundaries without an API break.
   *
   * KNOWN GAP (sealed fields, bare-spec form): `where`/`orderBy`/`groupBy`
   * refuse a `sensitive` field at compile time, but a reducer over a sensitive
   * field in the BARE-SPEC form (e.g. `aggregate({ x: sum('ssn') })`) is NOT
   * refused — the reducer factories (`sum`/`min`/`max`/…) are standalone
   * `(field: string)` functions with no collection-type context. This form is
   * preserved as-is for backward compatibility.
   *
   * The BUILDER form closes this gap: `aggregate(b => ({ x: b.sum('field') }))`
   * types the builder's field parameter as `QueryField<T, S>`, refusing any
   * `sensitive` field at compile time. Use the builder form for new code that
   * aggregates over a collection with sensitive fields.
   *
   */
  aggregate<Spec extends AggregateSpec>(spec: Spec): Aggregation<AggregateResult<Spec>>
  aggregate<Spec extends AggregateSpec>(build: (b: ReducerBuilder<T, S, M>) => Spec): Aggregation<AggregateResult<Spec>>
  aggregate<Spec extends AggregateSpec>(
    specOrBuild: Spec | ((b: ReducerBuilder<T, S, M>) => Spec),
  ): Aggregation<AggregateResult<Spec>> {
    let spec = typeof specOrBuild === 'function'
      ? (specOrBuild as (b: ReducerBuilder<T, S, M>) => Spec)((reducerBuilder as unknown) as ReducerBuilder<T, S, M>)
      : specOrBuild
    // Rewrite sum/min/max over Via-covered fields (e.g. money) into exact
    // BigInt reducers before the strategy runs (covers static run() and
    // live/MV paths).
    spec = this.source.via?.wrapReducers(spec) ?? spec
    // Closure over the current query. Produces the record set that
    // the aggregation reduces — same pipeline as `count()`, skipping
    // limit/offset because aggregation is over the full match set,
    // not a paginated slice. (A paginated aggregation would be a
    // different operation; see docs for rationale.)
    const source = this.source
    const clauses = this.plan.clauses
    const joinCtx = this.joinContext
    const hasCrossJoins = clauses.some(c => c.type === 'crossJoin')
    const executeRecords = (): readonly unknown[] => {
      if (hasCrossJoins) {
        if (!joinCtx) throw new Error('Query.aggregate(): crossJoin requires a join context')
        return executeClausePipeline(source, clauses, joinCtx)
      }
      const { candidates, remainingClauses } = candidateRecords(source, clauses)
      return remainingClauses.length === 0
        ? candidates
        : filterRecords(candidates, remainingClauses, fnViewDecoder(source))
    }

    // Upstream for live mode — only the left source subscribes.
    // Joined aggregations are out of scope for (see above), so
    // there are no right-side change streams to merge in.
    const upstreams: AggregationUpstream[] = []
    if (source.subscribe) {
      const subscribe = source.subscribe.bind(source)
      upstreams.push({ subscribe: (cb: () => void) => subscribe(cb) })
    }

    return this.aggregateStrategy.aggregate<Spec>(executeRecords, spec, upstreams)
  }

  /**
   * Partition matching records into buckets keyed by a field, then
   * terminate with `.aggregate(spec)` to compute per-bucket
   * reducers..
   *
   * ```ts
   * const byClient = invoices.query()
   *   .where('status', '==', 'open')
   *   .groupBy('clientId')
   *   .aggregate({ total: sum('amount'), n: count() })
   *   .run()
   * // → [ { clientId: 'c1', total: 5250, n: 3 }, … ]
   * ```
   *
   * Result rows carry the group key value under the grouping field
   * name plus every reducer output from the spec. Buckets are
   * emitted in first-seen order — consumers who want a specific
   * ordering should `.sort()` downstream.
   *
   * **Cardinality caps:** a one-shot warning fires at 10_000
   * distinct groups; `GroupCardinalityError` throws at 100_000.
   * Grouping on a high-uniqueness field like `id` or `createdAt` is
   * almost always a query mistake — the error message names the
   * field and observed cardinality and suggests narrowing with
   * `.where()` first.
   *
   * **Null / undefined keys:** records with a missing or explicitly
   * `null` group field get their own buckets. `Map`-based
   * partitioning distinguishes `undefined` from `null`, so the two
   * cases do NOT merge. Consumers who want them merged should
   * coalesce upstream with `.filter()`.
   *
   * **Joins are not applied** — same rationale as `.count()` and
   * `.aggregate()`. Joined fields in are projection-only, so
   * running a join inside a grouping pipeline would be wasteful and
   * could trigger `DanglingReferenceError` in strict mode for a
   * call whose intent is purely to bucket-and-reduce. Grouping by
   * a joined field is explicitly out of scope for — file an
   * issue if a real consumer needs it.
   *
   * **Filter clauses (`.filter(fn)`):** grouped queries still
   * support filter clauses in the underlying plan — they run in
   * the same candidate/filter pipeline that `.aggregate()` uses.
   * The performance caveat is the same: filter clauses cost O(N)
   * per record and can't be index-accelerated.
   */
  // The `field` param is `QueryField<T, S>` so a sealed (`sensitive`) field is
  // refused — grouping BY a sensitive field leaks its value distribution as
  // group-key labels. With `S = never` this is exactly `string` (zero churn).
  groupBy<F extends QueryField<T, S>>(field: F): GroupedQuery<T, F, S, M>
  groupBy<F extends readonly [QueryField<T, S>, QueryField<T, S>, ...QueryField<T, S>[]]>(
    ...fields: F
  ): GroupedQueryN<T, F, S, M>
  groupBy(...fields: readonly string[]): GroupedQuery<T, string, S, M> | GroupedQueryN<T, readonly string[], S, M> {
    if (fields.length === 0) {
      throw new Error('.groupBy() requires at least one field')
    }
    // Same record-producing closure as .aggregate() — grouped and
    // non-grouped aggregations execute over the same candidate set.
    // We inline the closure here instead of sharing a helper so the
    // builder stays allocation-friendly for the hot path.
    const source = this.source
    const clauses = this.plan.clauses
    const joinCtx = this.joinContext
    const hasCrossJoins = clauses.some(c => c.type === 'crossJoin')
    const executeRecords = (): readonly unknown[] => {
      if (hasCrossJoins) {
        if (!joinCtx) throw new Error('Query.groupBy(): crossJoin requires a join context')
        return executeClausePipeline(source, clauses, joinCtx)
      }
      const { candidates, remainingClauses } = candidateRecords(source, clauses)
      return remainingClauses.length === 0
        ? candidates
        : filterRecords(candidates, remainingClauses, fnViewDecoder(source))
    }

    const upstreams: AggregationUpstream[] = []
    if (source.subscribe) {
      const subscribe = source.subscribe.bind(source)
      upstreams.push({ subscribe: (cb: () => void) => subscribe(cb) })
    }

    // Dict-label resolution is single-field only — the <field>Label
    // projection has no meaningful shape for composite keys.
    if (fields.length === 1) {
      const field = fields[0]!
      const dictLabelResolver = buildDictLabelResolver(this.joinContext, field)
      return this.aggregateStrategy.groupBy<T, string, S, M>(
        executeRecords,
        field,
        upstreams,
        dictLabelResolver,
        this.source.via,
      )
    }
    return this.aggregateStrategy.groupByN<T, readonly string[], S, M>(
      executeRecords,
      fields,
      upstreams,
      this.source.via,
    )
  }

  /**
   * Re-run the query whenever the source notifies of changes.
   * Returns an unsubscribe function. The callback receives the latest result.
   * Throws if the source does not support subscriptions.
   *
   * **For joined queries, prefer `.live()`** — `subscribe()`
   * only re-fires on LEFT-side changes, so joined data can be
   * stale if the right side mutates between emissions. `.live()`
   * merges change streams from every join target.
   */
  subscribe(cb: (result: T[]) => void): () => void {
    if (!this.source.subscribe) {
      throw new Error('Query source does not support subscriptions. Pass a source with a subscribe() method.')
    }
    cb(this.toArray())
    return this.source.subscribe(() => cb(this.toArray()))
  }

  /**
   * Reactive terminal — returns a `LiveQuery<T>` that re-runs the
   * query and updates its `value` whenever any source feeding it
   * mutates..
   *
   * For non-joined queries, `.live()` is a convenience over the
   * existing `.subscribe()` callback shape: a hand-rolled reactive
   * primitive with `value` / `error` fields and a `subscribe(cb)`
   * notification channel. Frame-agnostic — Vue / React / Solid
   * adapters wrap it in their own primitive.
   *
   * For joined queries, `.live()` additionally subscribes to every
   * join target's change stream. Mutations on a right-side
   * collection (insert / update / delete of a client referenced by
   * an invoice) re-fire the live query and re-evaluate every
   * dependent left row. Right-side targets are deduped by
   * collection name, so a chain that joins the same target twice
   * (e.g. billing client + shipping client → both 'clients') only
   * subscribes once.
   *
   * **Ref-mode behavior on right-side disappearance** — matches the
   * eager `.toArray()` contract from :
   *   - `strict`  → re-run throws `DanglingReferenceError`. The
   *     LiveQuery catches the throw, stores it in `live.error`, and
   *     notifies listeners (the throw does NOT propagate out of
   *     the source's change handler — that would tear down the
   *     emitter). Consumers check `live.error` after each
   *     notification and render an error state in the UI.
   *   - `warn`    → joined value flips to `null`; the existing
   *     warn-channel deduplication keeps repeated re-runs from
   *     spamming the console.
   *   - `cascade` → no special handling needed; the cascade-
   *     delete mechanism propagates the right-side delete into the
   *     left collection on the next tick, and the live query
   *     naturally re-fires with the orphaned left rows gone.
   *
   * Always call `live.stop()` when finished — it tears down every
   * upstream subscription. The Vue layer's `onUnmounted` hook
   * should call `stop()` automatically; raw consumers must do it
   * themselves.
   *
   * **Limitations:**
   *   - No granular delta updates — the whole query re-runs on
   *     every change.
   *   - No microtask batching — bursty changes produce one re-run
   *     per change.
   *   - No re-planning under live mutations — the planner picks
   *     once at subscription time and reuses the same plan.
   *   - Streaming live joins are deferred.
   */
  live(): LiveQuery<T> {
    const upstreams: LiveUpstream[] = []

    // Left-side change stream — every live query subscribes to
    // its source if the source supports subscriptions.
    if (this.source.subscribe) {
      const leftSubscribe = this.source.subscribe.bind(this.source)
      upstreams.push({
        subscribe: (cb: () => void) => leftSubscribe(cb),
      })
    }

    // Right-side change streams — only for joined queries. Dedup
    // by target name so a chain joining the same target twice
    // doesn't double-subscribe and double-fire on every right-side
    // mutation.
    if (this.plan.joins.length > 0 && this.joinContext) {
      const subscribed = new Set<string>()
      for (const leg of this.plan.joins) {
        if (subscribed.has(leg.target)) continue
        subscribed.add(leg.target)
        const rightSource = this.joinContext.resolveSource(leg.target)
        if (rightSource?.subscribe) {
          const rightSubscribe = rightSource.subscribe.bind(rightSource)
          upstreams.push({
            subscribe: (cb: () => void) => rightSubscribe(cb),
          })
        }
      }
    }

    // Cross-join right-side change streams — symmetric with FK joins above.
    if (this.joinContext) {
      const subscribedCross = new Set<string>()
      for (const clause of this.plan.clauses) {
        if (clause.type !== 'crossJoin') continue
        if (subscribedCross.has(clause.target)) continue
        subscribedCross.add(clause.target)
        const rightSource = this.joinContext.resolveSource(clause.target)
        if (rightSource?.subscribe) {
          const rightSubscribe = rightSource.subscribe.bind(rightSource)
          upstreams.push({
            subscribe: (cb: () => void) => rightSubscribe(cb),
          })
        }
      }
    }

    // The recompute is just toArray bound to this query — same
    // pipeline as eager execution, including join application.
    return buildLiveQuery<T>(() => this.toArray(), upstreams)
  }

  /**
   * Return the plan as a JSON-friendly object. FilterClause entries are
   * stripped (their `fn` cannot be serialized) and replaced with
   * { type: 'filter', fn: '[function]' } so devtools can still see them.
   */
  toPlan(): unknown {
    return serializePlan(this.plan)
  }
}

/**
 * Index-aware execution: try the indexed fast path first, fall back to a
 * full scan otherwise. Mirrors `executePlan` for the public surface but
 * takes a `QuerySource` so it can consult `getIndexes()` and `lookupById()`.
 */
function executePlanWithSource(
  source: InternalSource,
  plan: QueryPlan,
  joinContext?: JoinContext,
  locale?: string,
): unknown[] {
  const hasCrossJoins = plan.clauses.some(c => c.type === 'crossJoin')

  let result: unknown[]
  if (hasCrossJoins) {
    if (!joinContext) {
      throw new Error(
        `Query.toArray(): plan contains crossJoin clauses but no JoinContext is attached. ` +
          `Use collection.query() instead of new Query() for cross-join support.`,
      )
    }
    result = executeClausePipeline(source, plan.clauses, joinContext)
  } else {
    // Index-aware fast path: only the clauses NOT consumed by the index need
    // re-evaluation. For a single-clause query against an indexed field,
    // `remainingClauses` is empty and we skip per-record predicate evaluation.
    const { candidates, remainingClauses } = candidateRecords(source, plan.clauses)
    result =
      remainingClauses.length === 0
        ? [...candidates]
        : filterRecords(candidates, remainingClauses, fnViewDecoder(source))
  }

  if (plan.orderBy.length > 0) {
    // dictKey label-sort: for any `orderBy(..., { by: 'label' })`, build a
    // sync code→label resolver at the query locale so the sort compares
    // labels. `source.via` also feeds the #650 Task 7 matrix-tier fallback
    // (fields `joinContext.resolveDictSource` doesn't bridge).
    const labelMaps = buildOrderLabelMaps(plan.orderBy, joinContext, locale, source.via)
    result = sortRecords(result, plan.orderBy, source.via, labelMaps)
  }
  if (plan.offset > 0) {
    result = result.slice(plan.offset)
  }
  if (plan.limit !== undefined) {
    result = result.slice(0, plan.limit)
  }
  return result
}

interface CandidateResult {
  /** The reduced candidate set, materialized to record objects. */
  readonly candidates: readonly unknown[]
  /** The clauses that the index could not satisfy and must still be evaluated. */
  readonly remainingClauses: readonly Clause[]
}

/**
 * Pick a candidate record set using the index store when possible.
 *
 * Strategy: scan the top-level clauses for the FIRST `==` or `in` clause
 * against an indexed field. If found, use the index to materialize a
 * candidate set and return the OTHER clauses as `remainingClauses`. The
 * caller skips re-evaluating the index-driving clause because the index
 * is authoritative for that field.
 *
 * This is a deliberately simple planner. A future optimizer could pick
 * the most selective index, intersect multiple indexes, or push composite
 * keys through. For the single-index fast path is good enough.
 */
function candidateRecords(source: InternalSource, clauses: readonly Clause[]): CandidateResult {
  const indexes = source.getIndexes?.()
  if (!indexes || !source.lookupById || clauses.length === 0) {
    return { candidates: source.snapshot(), remainingClauses: clauses }
  }
  // Bind the lookup method through an arrow so it doesn't drift from
  // its `this` context — keeps the unbound-method lint rule happy.
  const lookupById = (id: string): unknown => source.lookupById?.(id)

  for (let i = 0; i < clauses.length; i++) {
    const clause = clauses[i]!
    if (clause.type !== 'field') continue
    if (!indexes.has(clause.field)) continue
    // A Via-covered clause (e.g. money) only carries a build-time
    // evaluator payload for per-record comparison by default — `buildClause`
    // does not rewrite `clause.value` into the index's stored representation
    // (e.g. multi-currency money operands are `{ amount, currency }`
    // objects; the index would stringify their keys to a no-match
    // sentinel). A binding MAY additionally supply `indexValue` (#625,
    // via `ViaBinding.indexProbe`) — the STORED-form operand for a direct
    // probe (fixed-mode money `==`/`in` today). When it's absent, skip the
    // index fast path for this clause; the fallback scan evaluates it via
    // `clause.via.evaluate`. MIXED-ERA DATA (money, #672 fixed, incl. the
    // #672 review's C1 finding): the index bucket this probe hits is no
    // longer keyed by the RAW stored string — EAGER-mode `CollectionIndexes`
    // canonicalizes money keys through `ViaPipeline.canonicalizeIndexKey` at
    // EVERY bucket-mutation site (`build`, `upsert`, `remove`), not just
    // build/rebuild, so a legacy non-canonical scaled value (predates the
    // field's money() declaration) lands in — and stays in — the SAME bucket
    // a canonical probe looks up, across later put()/delete() too. LAZY
    // mode's `PersistedCollectionIndex` gained the SAME guarantee (#677) —
    // its bucket-mutation sites (`ingest`/`upsert`/`remove`) canonicalize
    // through the same `ViaPipeline.canonicalizeIndexKey`, and its probe
    // path (`lazy-builder.ts`'s `resolveCandidateIds()`) canonicalizes the
    // `==`/`in` lookup value before calling `lookupEqual`/`lookupIn` — see
    // `moneyIndexProbe`'s doc comment (via-money/where.ts) for the full
    // story — lazy mode's post-filter is now Via-aware too (#684), so
    // end-to-end `lazyQuery().where()` money parity with this eager path
    // holds at every scale (`orderBy` ordering parity remains open, #695).
    if (clause.via && clause.via.indexValue === undefined) continue
    const probeValue = clause.via ? clause.via.indexValue : clause.value

    let ids: ReadonlySet<string> | null = null
    if (clause.op === '==') {
      ids = indexes.lookupEqual(clause.field, probeValue)
    } else if (clause.op === 'in' && Array.isArray(probeValue)) {
      ids = indexes.lookupIn(clause.field, probeValue)
    }

    if (ids !== null) {
      // Found an index-eligible clause: materialize the candidate set and
      // remove this clause from the remaining list.
      const remaining: Clause[] = []
      for (let j = 0; j < clauses.length; j++) {
        if (j !== i) remaining.push(clauses[j]!)
      }
      return {
        candidates: materializeIds(ids, lookupById),
        remainingClauses: remaining,
      }
    }
    // Not index-eligible — keep scanning in case a later clause is a
    // better candidate.
  }

  // No clause was index-eligible — fall back to a full scan.
  return { candidates: source.snapshot(), remainingClauses: clauses }
}

function materializeIds(
  ids: ReadonlySet<string>,
  lookupById: (id: string) => unknown,
): unknown[] {
  const out: unknown[] = []
  for (const id of ids) {
    const record = lookupById(id)
    if (record !== undefined) out.push(record)
  }
  return out
}

/**
 * Execute a plan against a snapshot of records.
 * Pure function — same input, same output, no side effects.
 *
 * Records are typed as `unknown` because plans are non-parametric; callers
 * cast the return type at the API surface (see `Query.toArray()`).
 */
export function executePlan(records: readonly unknown[], plan: QueryPlan): unknown[] {
  if (plan.clauses.some(c => c.type === 'crossJoin')) {
    throw new Error(
      `executePlan(): does not support crossJoin clauses. ` +
        `executePlan is a stateless pure function — it cannot resolve cross-join right-side ` +
        `collections. Use Query.toArray() (via collection.query()) instead.`,
    )
  }
  let result = filterRecords(records, plan.clauses)
  if (plan.orderBy.length > 0) {
    result = sortRecords(result, plan.orderBy)
  }
  if (plan.offset > 0) {
    result = result.slice(plan.offset)
  }
  if (plan.limit !== undefined) {
    result = result.slice(0, plan.limit)
  }
  return result
}

/**
 * Build the per-record DECODED view factory for user-callback clauses
 * (`filter` / `wherePredicate`) — those callbacks must see the canonical
 * shape (e.g. money's decimal string), never a Via-transformed stored
 * form. Field clauses are NOT affected: their operands were pre-built
 * into raw stored space at build time. Returns undefined when the
 * source's Via pipeline declares no result decode.
 */
function fnViewDecoder(source: InternalSource): ((r: unknown) => unknown) | undefined {
  const via = source.via
  if (!via || !via.hasResultDecode) return undefined
  return r => via.decodeResults(r)
}

function filterRecords(
  records: readonly unknown[],
  clauses: readonly Clause[],
  decodeForFns?: (r: unknown) => unknown,
): unknown[] {
  if (clauses.length === 0) return [...records]
  // Decode once per record, and only when a callback clause will consume it.
  const needsFnView = decodeForFns !== undefined && hasFnClause(clauses)
  const out: unknown[] = []
  for (const r of records) {
    const fnView = needsFnView ? decodeForFns(r) : undefined
    let matches = true
    for (const clause of clauses) {
      if (!evaluateClause(r, clause, fnView)) {
        matches = false
        break
      }
    }
    if (matches) out.push(r)
  }
  return out
}

/**
 * Walk the clause list in declaration order, batching filter clauses and
 * expanding on crossJoin clauses. Falls back to `candidateRecords + filterRecords`
 * (index fast-path) when no crossJoin clauses are present.
 *
 * Precondition: `joinContext` must be non-null when any `CrossJoinClause` is in `clauses`.
 */
function executeClausePipeline(
  source: InternalSource,
  clauses: readonly Clause[],
  joinContext: JoinContext,
): unknown[] {
  let rel: unknown[] = [...source.snapshot()]
  let filterBatch: Clause[] = []
  const decodeForFns = fnViewDecoder(source)

  for (const clause of clauses) {
    if (clause.type === 'crossJoin') {
      if (filterBatch.length > 0) {
        rel = filterRecords(rel, filterBatch, decodeForFns)
        filterBatch = []
      }
      const rightSource = joinContext.resolveSource(clause.target)
      if (!rightSource) {
        throw new CrossJoinSourceUnknownError(clause.target, joinContext.leftCollection)
      }
      rel = applyCrossJoin(rel, clause, rightSource)
    } else {
      filterBatch.push(clause)
    }
  }

  if (filterBatch.length > 0) {
    rel = filterRecords(rel, filterBatch, decodeForFns)
  }

  return rel
}

/**
 * Expand `leftRel` by cross-joining with `rightSource`. Enforces the cost ceiling
 * BEFORE allocating the expanded relation (full cartesian) or cumulatively
 * (lateral form). Throws `CrossJoinTooLargeError` on breach.
 */
function applyCrossJoin(
  leftRel: unknown[],
  clause: CrossJoinClause,
  rightSource: JoinableSource,
): unknown[] {
  const rightRows = rightSource.snapshot()
  const maxRows = clause.maxRows ?? DEFAULT_CROSS_JOIN_MAX_ROWS
  const { as } = clause

  if (!clause.on) {
    const product = leftRel.length * rightRows.length
    if (product > maxRows) {
      throw new CrossJoinTooLargeError({ target: clause.target, expected: product, limit: maxRows })
    }
    const expanded: unknown[] = []
    for (const left of leftRel) {
      const leftObj = left as Record<string, unknown>
      for (const right of rightRows) {
        expanded.push({ ...leftObj, [as]: right })
      }
    }
    return expanded
  }

  // Lateral — ceiling is cumulative (post-filter count)
  const expanded: unknown[] = []
  let cumulative = 0
  for (const left of leftRel) {
    const callbackResult = clause.on(left)
    let filteredRight: readonly unknown[]
    if (Array.isArray(callbackResult)) {
      filteredRight = callbackResult
    } else {
      filteredRight = (rightRows as unknown[]).filter(
        callbackResult as (r: unknown) => boolean,
      )
    }
    cumulative += filteredRight.length
    if (cumulative > maxRows) {
      throw new CrossJoinTooLargeError({
        target: clause.target,
        expected: cumulative,
        limit: maxRows,
      })
    }
    const leftObj = left as Record<string, unknown>
    for (const right of filteredRight) {
      expanded.push({ ...leftObj, [as]: right })
    }
  }
  return expanded
}

function sortRecords(
  records: unknown[],
  orderBy: readonly OrderBy[],
  via?: ViaPipeline,
  labelMaps?: Map<string, (code: string) => string | undefined>,
): unknown[] {
  // Stable sort: Array.prototype.sort is required to be stable since ES2019.
  return [...records].sort((a, b) => {
    for (const { field, direction, by } of orderBy) {
      let av = readField(a, field)
      let bv = readField(b, field)
      // dictKey/lookup label-sort: compare resolved labels (fallback to the
      // code when unresolved), so e.g. honorific codes sort by their locale label.
      const labelResolver = by === 'label' ? labelMaps?.get(field) : undefined
      if (labelResolver) {
        av = (typeof av === 'string' ? labelResolver(av) : undefined) ?? av
        bv = (typeof bv === 'string' ? labelResolver(bv) : undefined) ?? bv
        const cmp = compareValues(av, bv)
        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp
        continue
      }
      // A Via-covered field (e.g. money) may store a representation the
      // generic comparator would order wrong (money's scaled-integer
      // strings sort lexically, not numerically: '9882' > '10004'). Ask
      // the pipeline for an exact ordering first; fall back to the
      // generic comparator when no binding covers the field.
      const viaCmp = via?.compareForOrder(field, av, bv)
      const cmp = viaCmp !== undefined ? viaCmp : compareValues(av, bv)
      if (cmp !== 0) return direction === 'asc' ? cmp : -cmp
    }
    return 0
  })
}

/**
 * dictKey/lookup label-sort: for each `orderBy(..., { by: 'label' })`
 * field, build a sync per-key label RESOLVER at the query `locale` (falling
 * back to a `staticDict` `displayLocale`). Two sources, tried in order:
 *
 * 1. `joinContext.resolveDictSource(field)` — the pre-existing dict-registry
 *    bridge (unchanged from before #650 Task 7): covers dictKey/staticDict
 *    AND their native `dict()`/`lookup(static)`/`lookup(reserved)` aliases
 *    (`registry.ts`'s `collectLookupDictCompat` bridges those tiers into the
 *    same vault registries this reads). Builds an EAGER `code -> label` map
 *    from the source's full snapshot, same as always.
 * 2. `via.resolveOrderLabel(field, key, locale)` (#650 Task 7) — the
 *    fallback for lookup fields the bridge above doesn't cover (matrix/
 *    collection tier — no vault registry backs it). Per-key, LAZY (no full
 *    snapshot enumeration API at this layer), memoized per field so a
 *    repeated key within one sort doesn't re-resolve.
 *
 * Fields with neither source available are skipped — the sort then falls
 * back to the raw stored code for them.
 */
function buildOrderLabelMaps(
  orderBy: readonly OrderBy[],
  joinContext: JoinContext | undefined,
  locale: string | undefined,
  via: ViaPipeline | undefined,
): Map<string, (code: string) => string | undefined> | undefined {
  let maps: Map<string, (code: string) => string | undefined> | undefined
  for (const { field, by } of orderBy) {
    if (by !== 'label') continue
    const dictSource = joinContext?.resolveDictSource?.(field)
    if (dictSource) {
      const loc = locale ?? dictSource.displayLocale
      if (loc === undefined) continue
      const codeToLabel = new Map<string, string>()
      for (const entry of dictSource.snapshot()) {
        const k = (entry as Record<string, unknown>)['key']
        const labels = (entry as Record<string, unknown>)['labels'] as Record<string, string> | undefined
        const label = labels?.[loc]
        if (typeof k === 'string' && typeof label === 'string') codeToLabel.set(k, label)
      }
      ;(maps ??= new Map()).set(field, (code: string) => codeToLabel.get(code))
      continue
    }
    if (via) {
      const cache = new Map<string, string | undefined>()
      ;(maps ??= new Map()).set(field, (code: string) => {
        if (!cache.has(code)) cache.set(code, via.resolveOrderLabel(field, code, locale))
        return cache.get(code)
      })
    }
  }
  return maps
}

function readField(record: unknown, field: string): unknown {
  if (record === null || record === undefined) return undefined
  if (!field.includes('.')) {
    return (record as Record<string, unknown>)[field]
  }
  const segments = field.split('.')
  let cursor: unknown = record
  for (const segment of segments) {
    if (cursor === null || cursor === undefined) return undefined
    cursor = (cursor as Record<string, unknown>)[segment]
  }
  return cursor
}

function compareValues(a: unknown, b: unknown): number {
  // Nullish goes last in asc order.
  if (a === undefined || a === null) return b === undefined || b === null ? 0 : 1
  if (b === undefined || b === null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'string' && typeof b === 'string') return a < b ? -1 : a > b ? 1 : 0
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
  // Mixed/unsupported types: treat as equal so the sort stays stable.
  // (Deliberate choice — we don't try to coerce arbitrary objects to strings.)
  return 0
}

function serializePlan(plan: QueryPlan): unknown {
  return {
    clauses: plan.clauses.map(serializeClause),
    orderBy: plan.orderBy,
    limit: plan.limit,
    offset: plan.offset,
    joins: plan.joins,
  }
}

function serializeClause(clause: Clause): unknown {
  if (clause.type === 'filter') {
    return { type: 'filter', fn: '[function]' }
  }
  if (clause.type === 'wherePredicate') {
    // Strip the live `fn` reference (non-serializable) but keep the
    // identity-carrying fields so distinct predicates still serialize
    // distinctly. `predicateHash` + `ctxHash` are the hash identity;
    // `name` is the named predicate reference. This matters because
    // A previous fall-through (return clause) exposed the live fn and produced
    // identical serializations for distinct predicates with different ctx values.
    return {
      type: 'wherePredicate',
      name: clause.name,
      ctx: clause.ctx,
      predicateHash: clause.predicateHash,
      ctxHash: clause.ctxHash,
      fn: '[function]',
    }
  }
  if (clause.type === 'group') {
    return {
      type: 'group',
      op: clause.op,
      clauses: clause.clauses.map(serializeClause),
    }
  }
  if (clause.type === 'crossJoin') {
    return {
      type: 'crossJoin',
      target: clause.target,
      as: clause.as,
      on: clause.on ? '[function]' : undefined,
      onPredicateName: clause.onPredicateName,
      maxRows: clause.maxRows,
    }
  }
  return clause
}

/**
 * Compute a stable hash of a `ctx` value supplied to
 * `.wherePredicate(name, ctx)`. Canonical-JSON: keys sorted at each
 * level so `{a, b}` and `{b, a}` hash to the same value. Undefined ctx
 * hashes to the empty string. The hash is sync because it just runs
 * a cheap djb2-style fold — used at builder time, not security-sensitive.
 *
 * @internal
 */
function canonicalCtxHash(ctx: unknown): string {
  if (ctx === undefined) return ""
  const canonical = JSON.stringify(ctx, (_key, value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const sorted: Record<string, unknown> = {}
      for (const k of Object.keys(value as Record<string, unknown>).sort()) {
        sorted[k] = (value as Record<string, unknown>)[k]
      }
      return sorted
    }
    return value
  })
  // djb2 fold over the canonical string; converted to hex.
  let h = 5381
  for (let i = 0; i < canonical.length; i++) {
    h = ((h << 5) + h) ^ canonical.charCodeAt(i)
  }
  return (h >>> 0).toString(16).padStart(8, "0")
}

/**
 * Build a dict-label resolver for `Query.groupBy(field)` when the
 * grouping field is a `dictKey`. Extracted from the inline closure
 * inside `groupBy` so the multi-key path (which has no meaningful
 * `<field>Label` shape) can skip it cleanly. Pure refactor — no
 * behaviour change for the single-field path.
 *
 * Returns `undefined` when:
 *   - the join context lacks a `resolveDictSource` hook, or
 *   - no dictionary source is registered for `field`.
 *
 * @internal
 */
function buildDictLabelResolver(
  joinCtx: JoinContext | undefined,
  field: string,
):
  | ((key: string, locale: string, fallback?: string | readonly string[]) => Promise<string | undefined>)
  | undefined {
  if (!joinCtx?.resolveDictSource) return undefined
  const dictSource = joinCtx.resolveDictSource(field)
  if (!dictSource) return undefined
  const snapshot = dictSource.snapshot()
  // A staticDict-backed source carries a `displayLocale`; use it as the
  // locale default when the query is locale-less, so `{ by: 'label' }` still
  // resolves under a locale-less read. Plain _dict_* sources omit it.
  const displayLocale = dictSource.displayLocale
  const dictMap = new Map<string, Record<string, string>>()
  for (const entry of snapshot) {
    const k = (entry as Record<string, unknown>)['key']
    const labels = (entry as Record<string, unknown>)['labels']
    if (typeof k === 'string' && labels && typeof labels === 'object') {
      dictMap.set(k, labels as Record<string, string>)
    }
  }
  return async (
    key: string,
    locale: string,
    fallback?: string | readonly string[],
  ): Promise<string | undefined> => {
    const effLocale = locale || displayLocale
    if (!effLocale) return undefined
    const labels = dictMap.get(key)
    if (!labels) return undefined
    if (labels[effLocale] !== undefined) return labels[effLocale]
    const chain = Array.isArray(fallback)
      ? (fallback as readonly string[])
      : fallback
        ? [fallback as string]
        : []
    for (const fb of chain) {
      if (fb === 'any') {
        const any = Object.values(labels)[0]
        if (any !== undefined) return any
      } else if (labels[fb] !== undefined) {
        return labels[fb]
      }
    }
    return undefined
  }
}
